import { useCallback, useEffect, useRef, useState } from "react"
import {
  type StudentTimerMode,
  type StudentTimerPreference,
  type TimerStatus,
  computeTimerStatus,
  formatDurationLabel,
  readAttemptTimerRecord,
  readTimerPreference,
  updateAttemptTimerRecord,
  writeTimerPreference,
} from "./studentTimer"

export interface UseStudentTimerOptions {
  userId: string
  attemptId: string
  questionId: string | undefined
  /** Epoch ms when the current question was shown (context `startTime`). */
  questionStartedAt: number
  /** Instructor `time_limit_minutes` is set: whole-test mode is unavailable. */
  instructorTimeLimited: boolean
  isSubmitted: boolean
  /** An answer save is in flight; the per-question clock stands still. */
  paused: boolean
}

export interface StudentTimerStatus extends TimerStatus {
  limitSeconds: number
}

export interface StudentTimerState {
  preference: StudentTimerPreference
  setPreference: (next: StudentTimerPreference) => void
  effectiveMode: StudentTimerMode
  status: StudentTimerStatus | null
  /** Polite screen-reader message; changes only on phase transitions. */
  announcement: string
}

const OVERTIME_NOTE = "Keep going — your overtime will show in your review."

function playTimeUpChime() {
  const AudioContextCtor =
    window.AudioContext ??
    (window as Window & { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext
  if (!AudioContextCtor) return
  try {
    const context = new AudioContextCtor()
    const oscillator = context.createOscillator()
    const gain = context.createGain()
    oscillator.frequency.value = 880
    gain.gain.setValueAtTime(0.2, context.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.4)
    oscillator.connect(gain)
    gain.connect(context.destination)
    oscillator.onended = () => {
      void context.close()
    }
    oscillator.start()
    oscillator.stop(context.currentTime + 0.4)
  } catch {
    // Audio is optional; autoplay policies or missing APIs must not matter.
  }
}

export function useStudentTimer({
  userId,
  attemptId,
  questionId,
  questionStartedAt,
  instructorTimeLimited,
  isSubmitted,
  paused,
}: UseStudentTimerOptions): StudentTimerState {
  const [preferenceState, setPreferenceState] = useState(() => ({
    userId,
    preference: readTimerPreference(userId),
  }))
  if (preferenceState.userId !== userId) {
    setPreferenceState({ userId, preference: readTimerPreference(userId) })
  }
  const { preference } = preferenceState

  const setPreference = useCallback(
    (next: StudentTimerPreference) => {
      writeTimerPreference(userId, next)
      setPreferenceState({ userId, preference: next })
    },
    [userId],
  )

  const effectiveMode: StudentTimerMode =
    preference.mode === "whole_test" && instructorTimeLimited
      ? "off"
      : preference.mode
  const isActive = effectiveMode !== "off" && !isSubmitted
  const wholeTestActive = isActive && effectiveMode === "whole_test"
  const limitSeconds =
    effectiveMode === "whole_test"
      ? preference.wholeTestMinutes * 60
      : preference.perQuestionSeconds
  // The whole-test cleanup runs after the render that switched modes, so it
  // must read the whole-test target, never the per-question limit.
  const wholeTestLimitRef = useRef(preference.wholeTestMinutes * 60)
  wholeTestLimitRef.current = preference.wholeTestMinutes * 60

  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!isActive) return
    setNow(Date.now())
    const interval = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(interval)
  }, [isActive])

  // Per-question clock starts at the later of "question shown" and "timer
  // switched on", so enabling mid-question doesn't start in the red.
  const perQuestionEnabledAtRef = useRef<number | null>(null)
  if (isActive && effectiveMode === "per_question") {
    perQuestionEnabledAtRef.current ??= Date.now()
  } else {
    perQuestionEnabledAtRef.current = null
  }

  const [wholeTestStartedAt, setWholeTestStartedAt] = useState<number | null>(
    () => readAttemptTimerRecord(attemptId)?.wholeTest?.startedAt ?? null,
  )
  useEffect(() => {
    if (!wholeTestActive) return
    const record = updateAttemptTimerRecord(attemptId, (current) => ({
      ...current,
      wholeTest: current.wholeTest
        ? { ...current.wholeTest, limitSeconds: wholeTestLimitRef.current }
        : { startedAt: Date.now(), limitSeconds: wholeTestLimitRef.current },
    }))
    const startedAt = record.wholeTest?.startedAt ?? Date.now()
    setWholeTestStartedAt(startedAt)
    setNow(Date.now())
    // Freeze the used time whenever this clock stops: switched off, mode
    // changed, submitted, or the taking view unmounts.
    return () => {
      updateAttemptTimerRecord(attemptId, (current) => ({
        ...current,
        wholeTest: {
          startedAt,
          limitSeconds: wholeTestLimitRef.current,
          elapsedSeconds: Math.floor((Date.now() - startedAt) / 1000),
        },
      }))
    }
  }, [wholeTestActive, attemptId])

  // Pause the per-question clock while an answer save is in flight so network
  // latency isn't counted as time on the question. The interval is excluded
  // if the save fails and the student stays on the question.
  const pauseRef = useRef({
    key: "",
    pausedAt: null as number | null,
    offsetMs: 0,
  })
  const pauseKey = `${questionId ?? ""}:${questionStartedAt}`
  if (pauseRef.current.key !== pauseKey) {
    pauseRef.current = { key: pauseKey, pausedAt: null, offsetMs: 0 }
  }
  const pauseState = pauseRef.current
  if (paused && pauseState.pausedAt === null) {
    pauseState.pausedAt = Date.now()
  } else if (!paused && pauseState.pausedAt !== null) {
    pauseState.offsetMs += Date.now() - pauseState.pausedAt
    pauseState.pausedAt = null
  }

  let status: StudentTimerStatus | null = null
  if (isActive) {
    const startedAt =
      effectiveMode === "per_question"
        ? questionId === undefined
          ? null
          : Math.max(questionStartedAt, perQuestionEnabledAtRef.current ?? 0)
        : wholeTestStartedAt
    if (startedAt !== null) {
      const clockNow =
        effectiveMode === "per_question"
          ? (pauseState.pausedAt ?? now) - pauseState.offsetMs
          : now
      status = {
        ...computeTimerStatus(limitSeconds, (clockNow - startedAt) / 1000),
        limitSeconds,
      }
    }
  }

  const phase = status?.phase ?? null
  const overtimeSeconds = status?.overtimeSeconds ?? 0
  const statusLimitSeconds = status?.limitSeconds ?? 0
  useEffect(() => {
    if (
      effectiveMode !== "per_question" ||
      phase !== "overtime" ||
      overtimeSeconds <= 0 ||
      questionId === undefined
    ) {
      return
    }
    const existing =
      readAttemptTimerRecord(attemptId)?.questionOvertime[questionId]
    if (existing && existing.overtimeSeconds >= overtimeSeconds) return
    updateAttemptTimerRecord(attemptId, (current) => ({
      ...current,
      questionOvertime: {
        ...current.questionOvertime,
        [questionId]: {
          limitSeconds: statusLimitSeconds,
          overtimeSeconds,
        },
      },
    }))
  }, [
    attemptId,
    effectiveMode,
    overtimeSeconds,
    phase,
    questionId,
    statusLimitSeconds,
  ])

  const [announcement, setAnnouncement] = useState("")
  const transitionKey = `${effectiveMode}:${
    effectiveMode === "per_question" ? questionId ?? "" : ""
  }`
  const lastPhaseRef = useRef<{ key: string | null; phase: string | null }>({
    key: null,
    phase: null,
  })
  const remainingSeconds = status?.remainingSeconds ?? 0
  useEffect(() => {
    // Announce only on phase transitions; ticks within a phase are no-ops.
    const last = lastPhaseRef.current
    const keyChanged = last.key !== transitionKey
    const phaseChanged = keyChanged || last.phase !== phase
    lastPhaseRef.current = { key: transitionKey, phase }
    if (!phaseChanged) return

    const target =
      effectiveMode === "per_question" ? "question timer" : "test timer"
    if (phase === "warning") {
      setAnnouncement(
        `${formatDurationLabel(remainingSeconds)} left on your ${target}.`,
      )
    } else if (phase === "overtime") {
      setAnnouncement(
        effectiveMode === "per_question"
          ? `Time's up for this question. ${OVERTIME_NOTE}`
          : `Time's up on your test timer. ${OVERTIME_NOTE}`,
      )
      if (!keyChanged && preference.sound) playTimeUpChime()
    } else {
      setAnnouncement("")
    }
  }, [transitionKey, phase, effectiveMode, remainingSeconds, preference.sound])

  return { preference, setPreference, effectiveMode, status, announcement }
}
